import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/design-system/src'
import { CardSliceContent } from '../../../types/prismic.types'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { MarketingPageRoutes } from '@dtx-company/true-common/src/constants/routes'
import { RichText } from 'prismic-reactjs'
import { SecondaryButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TopNavBannerWrapper } from '../TopNavBannerWrapper/TopNavBannerWrapper'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { hexToRgb } from '@app/page/src/utils/theme'
import { prismicImageLoader } from '@app/common/src/utils/prismicImageLoader'
import { useProUpsellBannerState } from '@app/code/src/components/Homepage/ProUpsellBanner/ProUpsellBanner.hooks'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import Link from 'next/link'
import NextImage from 'next/image'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'

export interface ProUpsellBannerProps {
  data: CardSliceContent
  sectionId?: string
}

export const ProUpsellBanner: React.FC<ProUpsellBannerProps> = ({ data, sectionId }) => {
  const { r, g, b } = React.useMemo(
    () => hexToRgb(data.card_background_color),
    [data.card_background_color]
  )
  const backgroundGradient = `linear-gradient(270deg, rgba(${r}, ${g}, ${b}, 0), rgba(${r}, ${g}, ${b}, 1))`

  return (
    <Box
      backgroundColor={data.card_background_color}
      width="100%"
      pl="80px"
      height="min-content"
      id={sectionId}
    >
      <Box flexDirection="column" maxWidth="40%">
        <Spacer mt="16px" />
        <Text variant="display/small" as="h2" fontWeight="bold" color={data.card_font_color}>
          {RichText.asText(data.title)}
        </Text>
        <Spacer mt="4px" />
        <Text variant="body/medium" color={data.card_font_color}>
          {RichText.asText(data.description)}
        </Text>
        <Spacer mt="8px" />
        {data.link.url && (
          <>
            <Link href={data.link.url} data-heap-id="ProUpsellBannerLink" passHref legacyBehavior>
              <SecondaryButton
                width="max-content"
                border="none"
                backgroundColor={data.link_background_color}
                onClick={() => {
                  events.Flowcode_LoggedOutGenerator_Clicked_LowerBannerText({
                    linkText: RichText.asText(data.link_text)
                  })
                }}
              >
                <Text color={data.link_font_color} variant="button/general" fontWeight="bold">
                  {RichText.asText(data.link_text)}
                </Text>
              </SecondaryButton>
            </Link>
            <Spacer mt="16px" />
          </>
        )}
      </Box>

      <Box width="60%" borderWidth="2px">
        <Box display="block" position="relative" width="100%" minHeight="150px">
          {data.image.url && (
            <NextImage
              src={data.image.url}
              alt={data.image.alt || ''}
              loader={prismicImageLoader}
              fill
              sizes="50vw"
              style={{
                borderLeft: `2px solid ${data.card_background_color}`,
                objectFit: 'cover'
              }}
            />
          )}
          <Box
            display="block"
            position="absolute"
            background={backgroundGradient}
            top={0}
            left={0}
            right={0}
            bottom={0}
          />
        </Box>
      </Box>
    </Box>
  )
}

export const ProUpsellTopNavBanner: React.FC = () => {
  const { isLargeDesktop, isDesktop, isTablet, isMobile } = useScreenSizeBreakpoints()

  const { displayProUpsellTopNavBanner } = useProUpsellBannerState()
  if (!displayProUpsellTopNavBanner) return null

  return (
    <TopNavBannerWrapper data-testid="pro-upsell-banner-container">
      {(isTablet || isDesktop) && (
        <>
          <Spacer ml={['20px', '20px', '58px', '58px']} />
          <Text maxWidth={['280px', '100%', '100%']} fontSize={['14px', '16px', '16px']}>
            <strong>Unlock powerful features with a premium membership</strong>
          </Text>
          <Spacer ml="16px" />
          <Box alignItems="center" padding="8px" height="24px" borderRadius="12px" flexShrink={0}>
            <Image src={'/icons/checkmark-circle.svg'} height="15px" alt="" aria-hidden="true" />
            <Spacer ml="8px" />
            <Text maxWidth={['280px', '100%', '100%']} fontSize={['12px', '14px', '14px']}>
              Create more codes
            </Text>
          </Box>
        </>
      )}
      {isDesktop && (
        <Box alignItems="center" padding="8px" height="24px" borderRadius="12px" flexShrink={0}>
          <Image src={'/icons/checkmark-circle.svg'} height="15px" alt="" aria-hidden="true" />
          <Spacer ml="8px" />
          <Text maxWidth={['280px', '100%', '100%']} fontSize={['12px', '14px', '14px']}>
            Unlock more design options
          </Text>
        </Box>
      )}
      {isLargeDesktop && (
        <Box alignItems="center" padding="8px" height="24px" borderRadius="12px" flexShrink={0}>
          <Image src={'/icons/checkmark-circle.svg'} height="15px" alt="" aria-hidden="true" />
          <Spacer ml="8px" />
          <Text maxWidth={['280px', '100%', '100%']} fontSize={['12px', '14px', '14px']}>
            Get advanced analytics
          </Text>
        </Box>
      )}

      {isMobile && (
        <>
          <Spacer />
          <Box flexDirection="column" margin="0 auto 0 8px">
            <Text fontSize="16px" fontWeight={700}>
              Upgrade now to access powerful features
            </Text>
          </Box>
        </>
      )}

      <Box margin={['0 auto 0 8px', '0 24px 0 auto', '0 24px 0 auto']} flexShrink={0}>
        <Button
          size="small"
          color="primary"
          onClick={() => {
            fireAnalyticsEvent('Revenue_CodeManagement_Clicked_TopBannerUpgradeNow')
          }}
        >
          <Link
            href={{
              pathname: MarketingPageRoutes.PLANS,
              query: { from: window.location.pathname }
            }}
          >
            {isMobile ? 'Upgrade now' : 'Explore Plans'}
          </Link>
        </Button>
      </Box>
    </TopNavBannerWrapper>
  )
}
